import type { RootState } from 'editor/src/store/index';

import sendPostMessage from 'editor/src/util/postMessages/sendPostMessage';

import type { ProductVariation, SingleOptions } from '../types';

/**
 * Checks if attributes object has any entries.
 */
const hasAttributes = (attributes: Record<string, string> | undefined | null): boolean => {
  return !!attributes && Object.keys(attributes).length > 0;
};

/**
 * Checks if a variation exists locally for the given attributes.
 * Returns undefined if attributes is empty to prevent accidental matches.
 */
export const findLocalVariation = (
  variations: ProductVariation[],
  attributes: Record<string, string>,
): ProductVariation | undefined => {
  // Return undefined for empty attributes to prevent matching first variation
  if (!hasAttributes(attributes)) {
    return undefined;
  }

  return variations.find((variation) => Object.entries(attributes).every(([key, value]) => variation[key] === value));
};

/**
 * Requests a variant from the host app.
 * Returns false if validation fails (empty attributes).
 */
export const requestVariantFromHost = (
  attributes: Record<string, string>,
  previousSelection: SingleOptions,
): boolean => {
  if (!hasAttributes(attributes)) {
    return false;
  }

  sendPostMessage('variants.requestVariantByAttributes', {
    attributes,
    previousSelection,
  });

  return true;
};

/**
 * Checks if lazy variant loading is enabled.
 */
export const isLazyLoadingEnabled = (state: RootState): boolean => {
  return !!state.variants.configuration.useLazyVariantLoading;
};

/**
 * Gets the current selection as attributes.
 */
export const getCurrentAttributes = (state: RootState): Record<string, string> => {
  return { ...state.variants.selectedSingleOptions };
};
